import type { VNode } from 'vue';
import type { ValidationState } from '../../common/static-wrappers/interfaces/validation-interface';
import type { DropdownButtonItemArgs } from '../dropdown-button/dropdown-button-item';
import { Prop, toNative } from 'vue-facing-decorator';
import TsxComponent, { Component } from '../../app/vuetsx';
import { isNullOrEmpty } from '../../common/utils/is-null-or-empty';
import DropdownButtonItem from '../dropdown-button/dropdown-button-item';
import './css/form-item-wrapper.css';

export enum HintType {
	Default = 0,
	Label = 1,
}

export enum MarginType {
	Standard = 0,
	None = 1,
	Small = 2,
}

export interface FormItemWrapperArgs {
	label?: string | VNode;
	mandatory?: boolean;
	hint?: string;
	hintType?: HintType;
	marginType?: MarginType;
	subtitle?: string;
	labelButtons?: DropdownButtonItemArgs[];
	flex?: boolean;
	cssClass?: string;
	maxWidth?: number;
	validationState?: ValidationState;
	showClearValueButton?: boolean;
	appendIcon?: string;
	prependIcon?: string;
	appendClicked?: () => void;
	prependClicked?: () => void;
	prependIconClicked?: () => void;
	appendIconClicked?: () => void;
	wrap?: boolean;
	labelInputRow?: boolean;
	errorId?: string;
}

export class FormItemWrapperConfig {
	static labelColumnClass: string = 'col-md-4';
	static contentColumnClass: string = 'col-md-8';
	static customRender: (this: typeof FormItemWrapper.prototype, h, instance?: typeof FormItemWrapper.prototype) => void;
}

@Component
class FormItemWrapperComponent extends TsxComponent<FormItemWrapperArgs> implements FormItemWrapperArgs {
	@Prop() label: string | VNode;
	@Prop() labelButtons: DropdownButtonItemArgs[];
	@Prop() mandatory: boolean;
	@Prop() flex: boolean;
	@Prop() hint: string;
	@Prop() subtitle: string;
	@Prop() hintType: HintType;
	@Prop() marginType?: MarginType;
	@Prop() maxWidth?: number;
	@Prop() appendIcon: string;
	@Prop() prependIcon: string;
	@Prop() cssClass: string;
	@Prop() showClearValueButton?: boolean;
	@Prop() appendClicked: () => void;
	@Prop() prependClicked: () => void;
	@Prop() prependIconClicked: () => void;
	@Prop() appendIconClicked: () => void;
	@Prop() wrap: boolean;
	@Prop() labelInputRow?: boolean;
	@Prop() errorId?: string;
	tooltipBound: boolean = false;

	mounted() {
		if (this.showClearValueButton) {
			this.bindClearValueButton();
		}
	}

	updated() {
		if (this.showClearValueButton) {
			this.handleCurrentInputState();
		}
	}

	handleAppendClicked() {
		if (this.appendClicked != null) {
			this.appendClicked();
		}
	}

	handlePrependIconClicked(): void {
		if (this.prependIconClicked != null) {
			this.prependIconClicked();
		}
	}

	handleAppendIconClicked(): void {
		if (this.appendIconClicked != null) {
			this.appendIconClicked();
		}
	}

	getFormGroupBaseClass(): string {
		let retVal: string = null;
		if (this.appendIcon == null && this.prependIcon == null && this.hint == null) {
			retVal = 'form-group';
		} else {
			retVal = 'input-group';
		}

		if (this.marginType != null) {
			retVal += ` fiw-margintype-${this.marginType}`;
		}

		return retVal;
	}

	getPropCssClass() {
		return (!isNullOrEmpty(this.cssClass) ? `${this.cssClass} ` : '');
	}

	getFlexCssClass() {
		return (this.flex != true ? '' : 'frm-item-flex' + ' ');
	}

	ensureTooltip() {
		if (!isNullOrEmpty(this.hint) && !this.tooltipBound) {
			this.$nextTick(() => {
				let target = $(this.$el).find('.input-group-append');
				if (this.hintType == HintType.Label) {
					target = $(this.$el).find('.col-form-label i');
				}

				if (!target.hasClass('tooltip-bound')) {
					target.addClass('tooltip-bound');
					target.tooltip();
				}
			});
		}
	}

	bindClearValueButton() {
		let elem: HTMLInputElement = this.$el.querySelector('.form-control');
		if (elem == null) {
			elem = this.$el.querySelector('input');
		}

		if (elem == null) {
			throw new Error('Unable to auto-bind to given control, has to be bound manually');
		}

		const button: HTMLElement = this.$el.querySelector('.fiw-clear-value-button');
		const valueCheck = () => {
			const val = elem.value;
			if (val) {
				button.classList.add('fiw-clear-value-button-visible');
			} else {
				button.classList.remove('fiw-clear-value-button-visible');
			}
		};

		const clearInput = () => {
			$(elem).val('');
			elem.dispatchEvent(new Event('change'));
		};

		this.handleCurrentInputState = valueCheck;
		this.clearInputState = clearInput;
		$(elem).on('change', valueCheck);
	}

	// overriden in bindClearValueButton
	handleCurrentInputState() { }

	// overriden in bindClearValueButton
	clearInputState() { }

	render(h) {
		if (FormItemWrapperConfig.customRender != null) {
			return FormItemWrapperConfig.customRender.call(
				this,
				h,
				this,
			);
		}

		if (this.wrap != false) {
			const isHintLabel = this.hint && this.hintType == HintType.Label;
			this.ensureTooltip();

			return (
				<div class={`${this.labelInputRow === false ? 'column' : 'row'} form-item-wrapper`}>
					<label class={`${FormItemWrapperConfig.labelColumnClass} col-form-label`}>
						{this.label}

						{!isNullOrEmpty(this.labelButtons) && this.labelButtons.map(btn => (
							<span class="col-form-labelbuttons">
								<DropdownButtonItem
									text={btn.text}
									clicked={btn.clicked}
									disabled={btn.disabled}
									href={btn.href}
									hrefBlank={btn.hrefBlank}
									icon={btn.icon}
									img={btn.img}
									isSelected={btn.isSelected}
									emptySpace={false}
								/>
							</span>
						))}

						{this.mandatory
						&& <span class="is-required"></span>}

						{isHintLabel
						&& <i class="icon icon-info input-group-append ms-1" style="cursor: help;" title={this.hint}></i>}
					</label>
					<div class={`${FormItemWrapperConfig.contentColumnClass}`}>
						{this.maxWidth == null
							? this.renderInnerContent(h)
							: (<div style={`max-width:${this.maxWidth}px;`}>{this.renderInnerContent(h)}</div>)}

					</div>
				</div>
			);
		} else {
			return (<div class="formwrap-nomargin">{this.renderInnerContent(h)}</div>);
		}
	}

	renderInnerContent(h) {
		return (
			<div class={`${this.getFormGroupBaseClass()} maxwidth-input ${this.getPropCssClass()}${this.getFlexCssClass()}${this.validationCssClass}`}>
				{this.prependIcon
				&& <div class="input-group-text prepend-icon" style="cursor: pointer;" onClick={() => { this.handlePrependIconClicked(); }}><i class={this.prependIcon}></i></div>}

				{this.$slots.default?.()}
				{this.hint && this.hintType != HintType.Label
				&& <div onClick={() => { this.handleAppendClicked(); }} title={this.hint} class="input-group-append" style="cursor: help;"><div class="input-group-text" style=""><i class="now-ui-icons travel_info"></i></div></div>}

				{this.showClearValueButton
				&& <div onClick={() => { this.clearInputState(); }} class={`form-control fiw-clear-value-button${this.appendIcon ? ' fiw-clear-append-icon' : ''}`}>×</div>}

				{this.appendIcon
				&& <div class="input-group-text append-icon" style="cursor: pointer;" onClick={() => { this.handleAppendIconClicked(); }}><i class={this.appendIcon}></i></div>}

				{this.hasValidationError
				&& <div class="invalid-feedback" id={this.errorId || undefined} role="alert">{this.validationErrorMessage}</div>}

				{!isNullOrEmpty(this.subtitle)
				&& <small class="form-text text-muted col-form-subtitle">{this.subtitle}</small>}
			</div>
		);
	}
}

const FormItemWrapper = toNative(FormItemWrapperComponent);
export default FormItemWrapper;
